<template>
  <v-dialog :value="value" @input="$emit('input', $event)" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="1040" scrollable>
    <v-card v-if="product" dark class="product-dialog-card">
      <v-btn icon class="close-btn" aria-label="Produktdetails schliessen" @click="$emit('input', false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <div class="product-detail-layout">
        <section class="gallery-panel" aria-label="Produktbilder">
          <div class="gallery-stage">
            <div v-if="imageLoading && !currentImageUrl" class="gallery-loading" aria-hidden="true"></div>

            <button
              v-else-if="currentImageUrl"
              type="button"
              class="gallery-open"
              :aria-label="`${product.title} in Vollbild anzeigen`"
              @click="openFullscreen">
              <transition name="image-fade" mode="out-in">
                <img
                  :key="currentImageUrl"
                  :src="currentImageUrl"
                  :alt="`${product.title}, Produktbild ${carouselIndex + 1}`"
                  class="gallery-image"
                  @error="handleImageError(currentImageItem)" />
              </transition>
            </button>

            <div v-else class="no-image-placeholder" role="img" :aria-label="`Kein Vorschaubild für ${product.title}`">
              <v-icon size="72" color="grey darken-1">
                {{ isDigital ? "mdi-file-pdf-box" : "mdi-image-off-outline" }}
              </v-icon>
              <span>Keine Vorschau verfügbar</span>
            </div>

            <v-chip v-if="isDigital" small color="primary" dark class="type-chip-overlay">
              <v-icon left x-small>mdi-download</v-icon>
              Download
            </v-chip>

            <v-btn v-if="currentImageUrl" icon class="fullscreen-btn" aria-label="Produktbild in Vollbild anzeigen" @click.stop="openFullscreen">
              <v-icon>mdi-fullscreen</v-icon>
            </v-btn>

            <template v-if="imageItems.length > 1">
              <v-btn icon class="gallery-nav gallery-nav-prev" aria-label="Vorheriges Produktbild" @click.stop="selectPreviousImage">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn icon class="gallery-nav gallery-nav-next" aria-label="Nächstes Produktbild" @click.stop="selectNextImage">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </template>

            <div v-if="imageItems.length > 1" class="gallery-counter" aria-live="polite">{{ carouselIndex + 1 }} / {{ imageItems.length }}</div>
          </div>

          <div v-if="imageItems.length > 1" class="thumbnail-list" role="list" aria-label="Weitere Produktbilder">
            <button
              v-for="(item, idx) in imageItems"
              :key="item.fileId || idx"
              type="button"
              role="listitem"
              class="thumbnail-button"
              :class="{ active: idx === carouselIndex }"
              :aria-label="`Produktbild ${idx + 1} anzeigen`"
              :aria-current="idx === carouselIndex ? 'true' : null"
              @click="selectImage(idx)">
              <img :src="item.url" alt="" @error="handleImageError(item)" />
            </button>
          </div>
        </section>

        <section class="purchase-panel" :aria-labelledby="productTitleId">
          <div class="purchase-sticky">
            <div class="product-eyebrow">
              <v-icon small color="grey lighten-1">{{ isDigital ? "mdi-file-download-outline" : "mdi-package-variant-closed" }}</v-icon>
              <span>{{ isDigital ? "Digitales Werbemittel" : "Physisches Werbemittel" }}</span>
            </div>

            <h2 :id="productTitleId" class="product-title">{{ product.title }}</h2>

            <div class="product-quantity-info">
              <span v-if="isDigital">PDF-Download</span>
              <span v-else>Bestellmenge {{ minimumQuantity }} – {{ product.maxQuantity || 100 }} Stück</span>
            </div>

            <div v-if="isSoldOut || isLowStock" class="stock-status" :class="isSoldOut ? 'sold-out' : 'low-stock'" role="status" aria-live="polite">
              <v-icon small :color="isSoldOut ? 'red lighten-2' : 'orange lighten-1'">
                {{ isSoldOut ? "mdi-close-circle-outline" : "mdi-alert-outline" }}
              </v-icon>
              <span>{{ isSoldOut ? "Diese Auswahl ist aktuell ausverkauft" : `Nur noch ${currentStock} Stück verfügbar` }}</span>
            </div>

            <div v-if="product.description" class="product-description">{{ product.description }}</div>

            <div v-if="hasOptions" class="options-selection">
              <div class="section-label">Variante wählen</div>
              <v-select
                v-for="opt in selectableOptions"
                :key="opt._id || opt.name"
                v-model="selectedOptionsMap[opt.name]"
                :items="getChoiceItems(opt)"
                item-text="label"
                item-value="value"
                item-disabled="disabled"
                :label="opt.name + ' *'"
                :aria-label="`${opt.name} für ${product.title} wählen`"
                :menu-props="{ offsetY: true }"
                outlined
                dense
                dark
                hide-details
                class="option-select"
                @change="handleOptionChange(opt, $event)" />
              <div class="variant-help">
                <v-icon x-small color="grey lighten-1">mdi-information-outline</v-icon>
                Nicht verfügbare Varianten sind als ausverkauft markiert.
              </div>
            </div>

            <v-divider class="purchase-divider" dark></v-divider>

            <div v-if="!isDigital" class="qty-selector">
              <div>
                <div class="section-label mb-0">Anzahl</div>
                <div class="qty-hint">Mindestens {{ minimumQuantity }} Stück</div>
              </div>
              <div class="qty-stepper-large" aria-label="Bestellmenge">
                <v-btn
                  icon
                  class="qty-control"
                  :aria-label="`Menge für ${product.title} verringern`"
                  @click="decreaseQty"
                  :disabled="qty <= minimumQuantity || isSoldOut">
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
                <input
                  v-model.number="qty"
                  type="number"
                  class="qty-input-large"
                  :aria-label="`Menge für ${product.title}`"
                  :min="minimumQuantity"
                  :max="maxAllowedQty"
                  :disabled="isSoldOut"
                  @blur="clampQty" />
                <v-btn
                  icon
                  class="qty-control"
                  :aria-label="`Menge für ${product.title} erhöhen`"
                  @click="increaseQty"
                  :disabled="qty >= maxAllowedQty || isSoldOut">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </div>
            </div>

            <div v-else class="digital-info">
              <v-icon color="primary" class="mr-3">mdi-information-outline</v-icon>
              <span>Nach dem Absenden findest du die Datei unter „Meine Bestellungen“. Sie wird dir zusätzlich per E-Mail zugestellt.</span>
            </div>

            <div v-if="cartQuantity > 0" class="cart-status" role="status">
              <v-icon small color="success">mdi-cart-check</v-icon>
              <span v-if="isDigital">Bereits im Warenkorb</span>
              <span v-else>Bereits {{ cartQuantity }} Stück im Warenkorb</span>
            </div>

            <v-btn
              block
              x-large
              color="primary"
              depressed
              rounded
              class="purchase-button"
              :aria-label="ctaAriaLabel"
              :disabled="!allOptionsSelected || isSoldOut"
              @click="addToCart">
              <v-icon left>{{ ctaIcon }}</v-icon>
              {{ ctaLabel }}
            </v-btn>
          </div>
        </section>
      </div>
    </v-card>

    <v-dialog v-model="fullscreenOpen" fullscreen dark overlay-color="black" overlay-opacity="0.95">
      <v-card class="fullscreen-card">
        <v-btn icon large class="close-fullscreen-btn" aria-label="Vollbild schliessen" @click="fullscreenOpen = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <img v-if="currentImageUrl" :src="currentImageUrl" :alt="product ? product.title : 'Produktbild'" class="fullscreen-image" />
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import api from "@/api"

export default {
  name: "ProductDetailDialog",
  props: {
    value: { type: Boolean, default: false },
    product: { type: Object, default: null },
    cartQuantity: { type: Number, default: 0 },
  },
  data() {
    return {
      qty: 1,
      imageItems: [],
      imageLoading: false,
      imageRequestId: 0,
      pendingImageFileId: null,
      selectedOptionsMap: {},
      carouselIndex: 0,
      fullscreenOpen: false,
    }
  },
  computed: {
    productTitleId() {
      return `product-detail-title-${this.product ? this.product._id || this._uid : this._uid}`
    },
    selectableOptions() {
      if (!this.product || !this.product.options) return []
      return this.product.options.filter(
        (option) => option.name && option.name.trim() && option.choices && option.choices.some((choice) => choice.value && choice.value.trim()),
      )
    },
    hasOptions() {
      return this.selectableOptions.length > 0
    },
    minimumQuantity() {
      return this.product && this.product.minQuantity ? this.product.minQuantity : 1
    },
    isDigital() {
      return this.product && this.product.productType === "digital"
    },
    currentStock() {
      if (!this.product) return null
      if (this.hasOptions) {
        if (!this.allOptionsSelected) return null

        const sig = this.getVariantSignature(this.selectedOptionsMap)
        if (!sig) return null
        if (!this.product.variantStock) return null
        const val = this.product.variantStock[sig]
        return val !== undefined ? val : null
      }
      return this.product.stock !== undefined && this.product.stock !== null ? this.product.stock : null
    },
    isSoldOut() {
      if (this.allOptionsSelected && this.currentStock !== null) {
        return this.currentStock < this.minimumQuantity
      }
      if (this.hasOptions) {
        if (!this.product.variantStock || Object.keys(this.product.variantStock).length === 0) {
          return false
        }
        const combinations = this.getProductCombinations(this.product)
        if (combinations.length === 0) return false

        let hasAvailable = false
        for (const sig of combinations) {
          const val = this.product.variantStock[sig]
          if (val === undefined || val === null || val >= this.minimumQuantity) {
            hasAvailable = true
            break
          }
        }
        return !hasAvailable
      }
      return this.product && this.product.stock !== undefined && this.product.stock !== null && this.product.stock < this.minimumQuantity
    },
    isLowStock() {
      if (this.currentStock === null || this.isSoldOut) return false
      const threshold = Math.max(10, this.minimumQuantity)
      return this.currentStock >= this.minimumQuantity && this.currentStock <= threshold
    },
    maxAllowedQty() {
      if (!this.product) return 100
      if (this.currentStock !== null) {
        return Math.max(this.minimumQuantity, Math.min(this.product.maxQuantity || 100, this.currentStock))
      }
      return this.product.maxQuantity || 100
    },
    currentImageItem() {
      if (!this.imageItems.length) return null
      return this.imageItems[this.carouselIndex] || this.imageItems[0]
    },
    currentImageUrl() {
      return this.currentImageItem ? this.currentImageItem.url : null
    },
    allOptionsSelected() {
      if (!this.hasOptions) return true
      return this.selectableOptions.every((option) => !!this.selectedOptionsMap[option.name])
    },
    ctaLabel() {
      if (this.isSoldOut) return "Ausverkauft"
      if (!this.allOptionsSelected) return "Variante wählen"
      return "In den Warenkorb"
    },
    ctaIcon() {
      if (this.isSoldOut) return "mdi-close-circle-outline"
      if (!this.allOptionsSelected) return "mdi-tune-variant"
      return "mdi-cart-plus"
    },
    ctaAriaLabel() {
      if (!this.product) return "In den Warenkorb"
      if (this.isSoldOut) return `${this.product.title} ist ausverkauft`
      if (!this.allOptionsSelected) return `Variante für ${this.product.title} wählen`
      const quantity = this.isDigital ? 1 : this.qty
      return `${quantity} Stück ${this.product.title} in den Warenkorb`
    },
  },
  watch: {
    value(open) {
      if (open && this.product) {
        this.qty = this.minimumQuantity
        this.selectedOptionsMap = this.selectableOptions.reduce((selections, option) => {
          selections[option.name] = null
          return selections
        }, {})
        this.carouselIndex = 0
        this.fullscreenOpen = false
        this.pendingImageFileId = null
        this.loadImages()
      } else if (!open) {
        this.cleanupImages()
        this.fullscreenOpen = false
      }
    },
  },
  methods: {
    async loadImages() {
      this.cleanupImages()
      if (!this.product || !this.product.images || this.product.images.length === 0) return

      const requestId = this.imageRequestId
      const productId = this.product._id
      const [firstImage, ...remainingImages] = this.product.images
      this.imageLoading = true

      try {
        const firstUrl = await api.marketingShop.loadProductImage(productId, firstImage.fileId)
        if (requestId !== this.imageRequestId) {
          URL.revokeObjectURL(firstUrl)
          return
        }
        this.imageItems = [{ fileId: firstImage.fileId, url: firstUrl }]
      } catch (e) {
        console.error("Erstes Produktbild konnte nicht geladen werden:", e)
      } finally {
        if (requestId === this.imageRequestId) this.imageLoading = false
      }

      if (requestId === this.imageRequestId && remainingImages.length > 0) {
        this.$nextTick(() => {
          if (requestId === this.imageRequestId) this.loadRemainingImages(remainingImages, productId, requestId)
        })
      }
    },
    async loadRemainingImages(images, productId, requestId) {
      const results = await Promise.all(
        images.map(async (image) => {
          try {
            const url = await api.marketingShop.loadProductImage(productId, image.fileId)
            return { fileId: image.fileId, url }
          } catch (e) {
            console.error("Weiteres Produktbild konnte nicht geladen werden:", e)
            return null
          }
        }),
      )

      const loadedItems = results.filter(Boolean)
      if (requestId !== this.imageRequestId) {
        loadedItems.forEach((item) => URL.revokeObjectURL(item.url))
        return
      }

      this.imageItems = this.imageItems.concat(loadedItems)
      this.selectPendingVariantImage()
    },
    cleanupImages() {
      this.imageRequestId += 1
      this.imageItems.forEach((item) => URL.revokeObjectURL(item.url))
      this.imageItems = []
      this.imageLoading = false
      this.carouselIndex = 0
    },
    handleImageError(item) {
      if (!item) return
      const index = this.imageItems.findIndex((image) => image.url === item.url)
      if (index === -1) return
      URL.revokeObjectURL(item.url)
      this.imageItems.splice(index, 1)
      if (index < this.carouselIndex) {
        this.carouselIndex -= 1
      } else if (this.carouselIndex >= this.imageItems.length) {
        this.carouselIndex = Math.max(0, this.imageItems.length - 1)
      }
    },
    selectImage(index) {
      this.carouselIndex = index
    },
    selectPreviousImage() {
      if (this.imageItems.length <= 1) return
      this.carouselIndex = (this.carouselIndex - 1 + this.imageItems.length) % this.imageItems.length
    },
    selectNextImage() {
      if (this.imageItems.length <= 1) return
      this.carouselIndex = (this.carouselIndex + 1) % this.imageItems.length
    },
    openFullscreen() {
      if (this.currentImageUrl) this.fullscreenOpen = true
    },
    handleOptionChange(opt, value) {
      this.$set(this.selectedOptionsMap, opt.name, value)
      const optionIndex = this.selectableOptions.findIndex((option) => option.name === opt.name)
      this.selectableOptions.slice(optionIndex + 1).forEach((option) => {
        this.$set(this.selectedOptionsMap, option.name, null)
      })
      this.pendingImageFileId = null
      const choice = (opt.choices || []).find((c) => c.value === value)
      if (choice && choice.imageFileId) {
        const idx = this.imageItems.findIndex((image) => image.fileId === choice.imageFileId)
        if (idx !== -1) {
          this.carouselIndex = idx
          this.pendingImageFileId = null
        } else {
          this.pendingImageFileId = choice.imageFileId
        }
      }
      this.$nextTick(() => {
        if (!this.isSoldOut) this.clampQty()
      })
    },
    selectPendingVariantImage() {
      if (!this.pendingImageFileId) return
      const index = this.imageItems.findIndex((image) => image.fileId === this.pendingImageFileId)
      if (index !== -1) {
        this.carouselIndex = index
        this.pendingImageFileId = null
      }
    },
    getChoiceItems(option) {
      return (option.choices || [])
        .filter((choice) => choice.value && choice.value.trim())
        .map((choice) => {
          const disabled = this.isChoiceUnavailable(option, choice)
          return {
            ...choice,
            disabled,
            label: disabled ? `${choice.value} — ausverkauft` : choice.value,
          }
        })
    },
    isChoiceUnavailable(option, choice) {
      if (!this.product || !this.product.variantStock || Object.keys(this.product.variantStock).length === 0) return false

      const selections = { ...this.selectedOptionsMap, [option.name]: choice.value }
      const combinations = this.getProductCombinations(this.product, selections)
      if (combinations.length === 0) return true

      return combinations.every((signature) => {
        const stock = this.product.variantStock[signature]
        return stock !== undefined && stock !== null && stock < this.minimumQuantity
      })
    },
    getVariantSignature(selections) {
      const choiceIds = []
      for (const option of this.selectableOptions) {
        const selectedValue = selections[option.name]
        if (!selectedValue) return ""
        const choice = (option.choices || []).find((item) => item.value === selectedValue)
        if (!choice || !choice.id) return ""
        choiceIds.push(choice.id)
      }
      return choiceIds.sort().join(",")
    },
    increaseQty() {
      this.qty = Math.min((this.qty || 1) + 1, this.maxAllowedQty)
    },
    decreaseQty() {
      this.qty = Math.max((this.qty || 1) - 1, this.minimumQuantity)
    },
    clampQty() {
      let v = parseInt(this.qty, 10) || this.minimumQuantity
      v = Math.max(this.minimumQuantity, Math.min(this.maxAllowedQty, v))
      this.qty = v
    },
    addToCart() {
      if (!this.allOptionsSelected) return
      if (this.isDigital) {
        this.$emit("add-to-cart", { product: this.product, quantity: 1, selectedOptions: { ...this.selectedOptionsMap } })
      } else {
        this.clampQty()
        this.$emit("add-to-cart", { product: this.product, quantity: this.qty, selectedOptions: { ...this.selectedOptionsMap } })
      }
      this.$emit("input", false)
    },
    getProductCombinations(product, selections = {}) {
      if (!product || !product.options || product.options.length === 0) return []
      const validOptions = product.options.filter((o) => o.name && o.name.trim() && o.choices && o.choices.some((c) => c.value && c.value.trim()))
      if (validOptions.length === 0) return []

      let results = [[]]
      for (const option of validOptions) {
        const nextResults = []
        const selectedValue = selections[option.name]
        const choices = option.choices.filter((c) => c.value && c.value.trim() && (!selectedValue || c.value === selectedValue))
        if (choices.length === 0) return []
        for (const res of results) {
          for (const choice of choices) {
            nextResults.push([...res, choice.id])
          }
        }
        results = nextResults
      }

      return results.map((comb) => comb.sort().join(","))
    },
  },
  beforeDestroy() {
    this.cleanupImages()
  },
}
</script>

<style scoped>
.product-dialog-card {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px !important;
  background: #171a18 !important;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.48) !important;
}

.product-detail-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.18fr) minmax(360px, 0.82fr);
  min-height: 0;
  max-height: 86vh;
  overflow-y: auto;
}

.close-btn {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 30;
  width: 42px !important;
  height: 42px !important;
  background: rgba(15, 20, 17, 0.76) !important;
  color: #fff !important;
  backdrop-filter: blur(10px);
}

.gallery-panel {
  min-width: 0;
  align-self: start;
  padding: 24px;
  background: radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.055), transparent 58%), #202421;
}

.gallery-stage {
  position: relative;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  aspect-ratio: 4 / 3;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
}

.gallery-open {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: zoom-in;
}

.gallery-open:focus-visible,
.thumbnail-button:focus-visible {
  outline: 3px solid #009640;
  outline-offset: -3px;
}

.gallery-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  object-position: center;
  background: transparent;
}

.gallery-loading {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.045);
}

.gallery-loading::after {
  position: absolute;
  inset: 0;
  content: "";
  background: linear-gradient(100deg, transparent 20%, rgba(255, 255, 255, 0.11) 48%, transparent 76%);
  transform: translateX(-100%);
  animation: gallery-shimmer 1.25s ease-in-out infinite;
}

@keyframes gallery-shimmer {
  to {
    transform: translateX(100%);
  }
}

.type-chip-overlay {
  position: absolute;
  z-index: 10;
  top: 14px;
  left: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

.fullscreen-btn,
.gallery-nav {
  position: absolute;
  z-index: 10;
  color: #fff !important;
  background: rgba(15, 20, 17, 0.72) !important;
  backdrop-filter: blur(8px);
}

.fullscreen-btn {
  top: 12px;
  right: 12px;
}

.gallery-nav {
  top: 50%;
  transform: translateY(-50%);
}

.gallery-nav-prev {
  left: 12px;
}

.gallery-nav-next {
  right: 12px;
}

.gallery-counter {
  position: absolute;
  z-index: 10;
  right: 14px;
  bottom: 14px;
  padding: 5px 9px;
  border-radius: 999px;
  color: #fff;
  background: rgba(15, 20, 17, 0.72);
  font-size: 11px;
  font-weight: 700;
  backdrop-filter: blur(8px);
}

.no-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.62);
  font-size: 13px;
  font-weight: 600;
}

.thumbnail-list {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  margin-top: 14px;
  padding: 2px 2px 8px;
  scrollbar-width: thin;
}

.thumbnail-button {
  flex: 0 0 72px;
  width: 72px;
  height: 58px;
  overflow: hidden;
  padding: 3px;
  border: 2px solid transparent;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.045);
  cursor: pointer;
  transition: border-color 160ms ease, background-color 160ms ease, transform 160ms ease;
}

.thumbnail-button:hover,
.thumbnail-button.active {
  border-color: #009640;
  background: rgba(255, 255, 255, 0.1);
}

.thumbnail-button.active {
  transform: translateY(-2px);
}

.thumbnail-button img {
  width: 100%;
  height: 100%;
  display: block;
  border-radius: 6px;
  object-fit: contain;
  background: transparent;
}

.purchase-panel {
  min-width: 0;
  background: #171a18;
}

.purchase-sticky {
  position: sticky;
  top: 0;
  padding: 36px 34px 32px;
}

.product-eyebrow {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 12px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.product-title {
  max-width: calc(100% - 34px);
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 750;
  color: #fff;
  line-height: 1.18;
  letter-spacing: -0.02em;
  margin: 0 0 8px;
}

.product-quantity-info {
  color: rgba(255, 255, 255, 0.58);
  font-size: 13px;
  font-weight: 600;
}

.stock-status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 14px;
  padding: 8px 11px;
  border: 1px solid;
  border-radius: 9px;
  font-size: 12px;
  font-weight: 700;
}

.stock-status.low-stock {
  color: #ffbd6a;
  border-color: rgba(255, 189, 106, 0.28);
  background: rgba(255, 153, 31, 0.08);
}

.stock-status.sold-out {
  color: #ff9a9a;
  border-color: rgba(255, 99, 99, 0.3);
  background: rgba(255, 82, 82, 0.08);
}

.product-description {
  margin-top: 22px;
  font-size: 14px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.76);
  white-space: pre-wrap;
}

.options-selection {
  margin-top: 24px;
}

.section-label {
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-weight: 700;
}

.option-select {
  margin-top: 10px;
}

.variant-help {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.52);
  font-size: 11.5px;
  line-height: 1.45;
}

.purchase-divider {
  margin: 26px 0 20px;
}

.qty-selector {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.qty-hint {
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 11.5px;
}

.qty-stepper-large {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 999px;
  padding: 2px;
}

.qty-control {
  width: 44px !important;
  height: 44px !important;
}

.qty-input-large {
  width: 54px;
  height: 44px;
  text-align: center;
  background: transparent;
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  outline: none;
  -moz-appearance: textfield;
}

.qty-input-large::-webkit-outer-spin-button,
.qty-input-large::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.digital-info {
  display: flex;
  align-items: flex-start;
  background: rgba(0, 150, 64, 0.1);
  border: 1px solid rgba(74, 222, 128, 0.2);
  padding: 14px;
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 12.5px;
  line-height: 1.5;
}

.cart-status {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 16px;
  color: #73dfa0;
  font-size: 12.5px;
  font-weight: 650;
}

.purchase-button {
  min-height: 52px;
  margin-top: 20px;
  text-transform: none !important;
  font-weight: 750;
  letter-spacing: 0 !important;
  transition: background-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
}

.purchase-button:not(.v-btn--disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(0, 150, 64, 0.25);
}

.fullscreen-card {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000 !important;
}

.close-fullscreen-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 100;
  background: rgba(0, 0, 0, 0.6) !important;
  color: #fff !important;
}

.fullscreen-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.image-fade-enter-active,
.image-fade-leave-active {
  transition: opacity 160ms ease;
}

.image-fade-enter,
.image-fade-leave-to {
  opacity: 0;
}

@media (max-width: 959px) {
  .product-detail-layout {
    grid-template-columns: minmax(0, 1fr);
    max-height: 90vh;
  }

  .gallery-panel {
    padding: 18px;
  }

  .gallery-stage {
    aspect-ratio: 16 / 10;
  }

  .purchase-sticky {
    position: static;
    padding: 28px;
  }
}

@media (max-width: 599px) {
  .product-dialog-card {
    min-height: 100%;
    border: 0;
    border-radius: 0 !important;
  }

  .product-detail-layout {
    min-height: 100%;
    max-height: none;
  }

  .close-btn {
    top: max(10px, env(safe-area-inset-top));
    right: 10px;
  }

  .gallery-panel {
    padding: 0 0 12px;
  }

  .gallery-stage {
    aspect-ratio: 1 / 1;
    border-radius: 0 0 16px 16px;
  }

  .thumbnail-list {
    margin: 10px 14px 0;
  }

  .purchase-sticky {
    padding: 24px 20px calc(24px + env(safe-area-inset-bottom));
  }

  .product-title {
    max-width: calc(100% - 28px);
    font-size: 24px;
  }

  .qty-selector {
    align-items: flex-start;
    flex-direction: column;
  }

  .qty-stepper-large {
    width: 100%;
    justify-content: space-between;
  }
}

@media (prefers-reduced-motion: reduce) {
  .thumbnail-button,
  .purchase-button,
  .image-fade-enter-active,
  .image-fade-leave-active {
    transition: none;
  }

  .thumbnail-button.active,
  .purchase-button:not(.v-btn--disabled):hover {
    transform: none;
  }

  .gallery-loading::after {
    animation: none;
  }
}
</style>
